
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, PropType } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { WxWebLoginService } from './wx-web-login.service'
import { LoginService, FastLoginResponseData } from '../login.service'
import { login_type_info } from '@/services/config.service'

/** wxLogin.js 官方 SDK 地址（腾讯托管） */
const WX_LOGIN_SDK_URL = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'

/** 等待微信 iframe 完成加载的最长时间，避免异常网络下 loading 一直不结束 */
const WX_LOGIN_IFRAME_LOAD_TIMEOUT = 8000

/** 极端旧环境不支持 MutationObserver 时的 iframe 查询间隔 */
const WX_LOGIN_IFRAME_POLL_INTERVAL = 100

/** 微信登录二维码依赖的外部域名，用于提前建立 DNS/TLS 连接 */
const WX_LOGIN_RESOURCE_ORIGINS = ['https://res.wx.qq.com', 'https://open.weixin.qq.com']

/** 中转页路径（业务页与生产页同名同路径，由部署保证） */
const CALLBACK_PATH = '/wx-web-login-callback.html'

/**
 * 默认生产中转页地址（向微信开放平台备案的固定 redirect_uri）
 * 业务页通过 postmessage_domain 参数指定接收消息的父页面 origin
 */
const DEFAULT_PROXY_URL = `https://pay.keniu.com${CALLBACK_PATH}`

/**
 * 业务环境 origin 白名单（必须与中转页 wx-web-login-callback.html 内
 * ALLOWED_BUSINESS_ORIGINS 完全一致；中转页白名单为最终防线，此处仅做客户端早期拦截）
 */
const ALLOWED_BUSINESS_ORIGINS: Array<string | RegExp> = [
  // 生产
  'https://pay.keniu.com',
  // 测试
  'https://kantu.aix-test-k8s.iweikan.cn',
  'http://kantu.aix-test-k8s.iweikan.cn',
  // 本地开发：localhost / 127.0.0.1 任意端口
  /^https?:\/\/localhost(:\d+)?$/,
  /^https?:\/\/127\.0\.0\.1(:\d+)?$/
]

/** 用于校验回调来源的消息类型，与中转页保持一致 */
const CALLBACK_MESSAGE_TYPE = 'wx-web-login-callback'

/** state 在 sessionStorage 中的存储 key */
const STATE_STORAGE_KEY = 'wxWebLoginState'

/**
 * 默认注入到 wxLogin.js iframe 内的样式表（base64 编码的 data URL）
 * 如需调整二维码尺寸，请同步修改本 base64 内容（解码后修改 .qrcode 的 width/height 再编码回去）
 * 解码后的源码示例：
 *   .impowerBox{text-align:center;}
 *   .impowerBox .qrcode{width:154px;height:154px;margin-top:0;border:none;}
 *   .impowerBox .title{display:none;}
 *   .impowerBox .info{display:none;}
 *   .impowerBox .status_icon{display:none;}
 *   .impowerBox .status{text-align:center;font-size:12px;}
 *   .toast,.toast_box,...{display:none!important;}
 */
const DEFAULT_IFRAME_CSS_DATA_URL =
  'data:text/css;base64,LmltcG93ZXJCb3h7dGV4dC1hbGlnbjpjZW50ZXI7fQouaW1wb3dlckJveCAucXJjb2Rle3dpZHRoOjE1NHB4O2hlaWdodDoxNTRweDttYXJnaW4tdG9wOjA7Ym9yZGVyOm5vbmU7fQouaW1wb3dlckJveCAudGl0bGV7ZGlzcGxheTpub25lO30KLmltcG93ZXJCb3ggLmluZm97ZGlzcGxheTpub25lO30KLmltcG93ZXJCb3ggLnN0YXR1c19pY29ue2Rpc3BsYXk6bm9uZTt9Ci5pbXBvd2VyQm94IC5zdGF0dXN7dGV4dC1hbGlnbjpjZW50ZXI7Zm9udC1zaXplOjEycHg7fQoudG9hc3QsLnRvYXN0X2JveCwudG9hc3RCb3gsLndldWktdG9hc3QsLndldWlfdG9hc3QsLnd4X3RvYXN0LC5qc190b2FzdCwucXJjb2RlX3RvYXN0LFtjbGFzcyo9InRvYXN0Il0sW2NsYXNzKj0iVG9hc3QiXSxbaWQqPSJ0b2FzdCJdLFtpZCo9IlRvYXN0Il17ZGlzcGxheTpub25lIWltcG9ydGFudDt2aXNpYmlsaXR5OmhpZGRlbiFpbXBvcnRhbnQ7b3BhY2l0eTowIWltcG9ydGFudDtwb2ludGVyLWV2ZW50czpub25lIWltcG9ydGFudDt9'

/** 加载阶段，控制 UI 渲染 */
type LoadStatus = 'loading' | 'ready' | 'error'

/** 等待微信 iframe 加载的可取消控制器 */
interface WxLoginIframeReadyWaiter {
  promise: Promise<void>
  cancel: () => void
}

/** 是否已注入微信登录资源预热标签 */
let hasWxLoginResourceHints = false

/** wxLogin.js 加载中的 Promise，避免重复创建 script 或重复等待 */
let wxLoginSdkPromise: Promise<void> | null = null

/** 全局 WxLogin 构造函数（由 wxLogin.js 注入） */
declare global {
  interface Window {
    WxLogin?: new (opts: Record<string, any>) => any
  }
}

/**
 * 注入微信登录相关的资源预热标签。
 * 这里不改变登录流程，只提前做 DNS 解析、TLS 建连和 SDK 预加载，
 * 在客户端 WebView 首次打开登录窗时可以减少等待外部资源的时间。
 */
function ensureWxLoginResourceHints() {
  if (hasWxLoginResourceHints || typeof document === 'undefined') return
  hasWxLoginResourceHints = true

  const appendLink = (attrs: Record<string, string>) => {
    const exists = Array.from(document.head.querySelectorAll('link')).some((link) => {
      return Object.entries(attrs).every(([key, value]) => link.getAttribute(key) === value)
    })

    if (exists) return

    const link = document.createElement('link')
    Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value))
    document.head.appendChild(link)
  }

  WX_LOGIN_RESOURCE_ORIGINS.forEach((origin) => {
    appendLink({ rel: 'dns-prefetch', href: origin.replace(/^https?:/, '') })
    appendLink({ rel: 'preconnect', href: origin })
  })
  appendLink({ rel: 'preload', href: WX_LOGIN_SDK_URL, as: 'script' })
}

/**
 * 校验 origin 是否在业务白名单
 */
function isBusinessOriginAllowed(origin: string): boolean {
  if (!origin) return false
  return ALLOWED_BUSINESS_ORIGINS.some((rule) => {
    if (typeof rule === 'string') return rule === origin
    return rule.test(origin)
  })
}

/**
 * 从 URL 中安全提取 origin，供 postMessage 来源校验使用。
 */
function getUrlOrigin(url: string): string {
  try {
    return new URL(url).origin
  } catch (err) {
    return ''
  }
}

/**
 * 生成符合微信文档要求的 state（a-zA-Z0-9，长度 ≤ 128 字节）
 * 优先使用 crypto.getRandomValues，降级使用 Math.random
 */
function generateState(): string {
  const len = 32
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''

  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const bytes = new Uint8Array(len)
    window.crypto.getRandomValues(bytes)
    for (let i = 0; i < len; i++) {
      result += charset[bytes[i] % charset.length]
    }
    return result
  }

  for (let i = 0; i < len; i++) {
    result += charset[Math.floor(Math.random() * charset.length)]
  }
  return result
}

/**
 * 动态注入 wxLogin.js 官方 SDK
 * 同一页面多次调用会复用已存在的 <script> 标签，避免重复加载
 */
function loadWxLoginSdk(): Promise<void> {
  ensureWxLoginResourceHints()

  if (typeof window.WxLogin === 'function') {
    return Promise.resolve()
  }

  if (wxLoginSdkPromise) {
    return wxLoginSdkPromise
  }

  wxLoginSdkPromise = new Promise((resolve, reject) => {
    if (typeof window.WxLogin === 'function') {
      resolve()
      return
    }

    const exist = document.querySelector<HTMLScriptElement>('script[data-wx-login-sdk="1"]')
    if (exist) {
      if (exist.dataset.wxLoginSdkLoaded === '1') {
        wxLoginSdkPromise = null
        reject(new Error('wxLogin.js 已加载但构造函数缺失'))
        return
      }
      exist.addEventListener('load', () => resolve(), { once: true })
      exist.addEventListener('error', () => {
        wxLoginSdkPromise = null
        reject(new Error('wxLogin.js 加载失败'))
      }, { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = WX_LOGIN_SDK_URL
    script.async = true
    script.defer = true
    script.dataset.wxLoginSdk = '1'
    script.onload = () => {
      script.dataset.wxLoginSdkLoaded = '1'
      script.onload = null
      script.onerror = null
      resolve()
    }
    script.onerror = () => {
      wxLoginSdkPromise = null
      script.onload = null
      script.onerror = null
      script.remove()
      reject(new Error('wxLogin.js 加载失败'))
    }
    document.head.appendChild(script)
  })

  return wxLoginSdkPromise
}

/**
 * 提前下载 wxLogin.js。
 * 该函数只做性能预热，失败时不改变 UI 状态；真正的错误仍由 renderQrcode 统一处理。
 */
function preloadWxLoginSdk(appid: string) {
  if (!appid || !isBusinessOriginAllowed(window.location.origin)) {
    ensureWxLoginResourceHints()
    return
  }

  loadWxLoginSdk().catch(() => undefined)
}

/**
 * 等待 wxLogin.js 注入的 iframe 完成加载。
 * wxLogin.js 的构造函数返回时只代表 iframe 已开始创建，不代表二维码内容已渲染完成；
 * 因此这里等 iframe 的 load 事件后再隐藏 loading 文案，避免出现一段空白二维码区域。
 */
function waitForWxLoginIframeReady(container: HTMLElement): WxLoginIframeReadyWaiter {
  let observer: MutationObserver | null = null
  let iframe: HTMLIFrameElement | null = null
  let timeoutId = 0
  let intervalId = 0
  let rafId = 0
  let settled = false
  let resolvePromise: () => void = () => undefined
  let rejectPromise: (err: Error) => void = (err: Error) => {
    throw err
  }

  const cleanup = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
    if (iframe) {
      iframe.removeEventListener('load', handleIframeLoad)
      iframe = null
    }
    if (timeoutId) {
      window.clearTimeout(timeoutId)
      timeoutId = 0
    }
    if (intervalId) {
      window.clearInterval(intervalId)
      intervalId = 0
    }
    if (rafId) {
      window.cancelAnimationFrame(rafId)
      rafId = 0
    }
  }

  const finish = (err?: Error) => {
    if (settled) return
    settled = true
    cleanup()
    if (err) {
      rejectPromise(err)
      return
    }
    resolvePromise()
  }

  const handleIframeLoad = () => {
    rafId = window.requestAnimationFrame(() => finish())
  }

  const bindIframeLoad = (nextIframe: HTMLIFrameElement) => {
    if (iframe === nextIframe) return
    if (iframe) {
      iframe.removeEventListener('load', handleIframeLoad)
    }
    iframe = nextIframe
    iframe.addEventListener('load', handleIframeLoad, { once: true })
  }

  const checkIframe = () => {
    const nextIframe = container.querySelector<HTMLIFrameElement>('iframe')
    if (nextIframe) {
      bindIframeLoad(nextIframe)
    }
  }

  const promise = new Promise<void>((resolve, reject) => {
    resolvePromise = resolve
    rejectPromise = reject
    timeoutId = window.setTimeout(() => {
      finish(new Error('微信二维码加载超时'))
    }, WX_LOGIN_IFRAME_LOAD_TIMEOUT)

    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(checkIframe)
      observer.observe(container, { childList: true, subtree: true })
    } else {
      intervalId = window.setInterval(checkIframe, WX_LOGIN_IFRAME_POLL_INTERVAL)
    }
    checkIframe()
  })

  return {
    promise,
    cancel: () => finish()
  }
}

export default defineComponent({
  name: 'WxWebLogin',
  props: {
    /**
     * 微信开放平台「网站应用」的 appid
     */
    appid: {
      type: String,
      required: true
    },
    /**
     * 生产中转页地址（即向微信开放平台备案的 redirect_uri，写死生产域）
     * - 默认值：https://pay.keniu.com/wx-web-login-callback.html
     * - 一般无需覆盖，仅在中转页域名/路径变更时使用
     * - 多环境无需修改：业务页 origin 会通过 postmessage_domain 参数传给中转页
     */
    proxyUrl: {
      type: String,
      default: DEFAULT_PROXY_URL
    },
    /**
     * 应用授权作用域，固定 snsapi_login（网站应用唯一可用 scope）
     */
    scope: {
      type: String,
      default: 'snsapi_login'
    },
    /**
     * 二维码尺寸（px）
     */
    size: {
      type: Number as PropType<number>,
      default: 154
    },
    /**
     * 自定义 iframe 内样式表 URL（透传给 wxLogin.js 的 href 参数）
     * - 默认：DEFAULT_IFRAME_CSS_DATA_URL（base64 内联样式，已隐藏标题/说明、缩放二维码到 154px）
     * - 自定义：可传任意 http/https 可访问、与登录页同协议的 CSS URL
     * - 显式传入空字符串 ''：使用 wxLogin.js 原始默认样式（不建议，会撑爆容器）
     */
    customCssUrl: {
      type: String,
      default: DEFAULT_IFRAME_CSS_DATA_URL
    }
  },
  setup(props) {
    const injector = useReflectiveInjector([WxWebLoginService])
    const service = injector.get(WxWebLoginService)
    const login = injector.get(LoginService)

    /** 给二维码挂载点一个唯一 id，避免同页多实例冲突 */
    const containerId = `wx-web-login-qrcode-${Math.random()
      .toString(36)
      .slice(2, 10)}`

    const status = ref<LoadStatus>('loading')
    const loadingText = ref('正在加载微信扫码...')
    const errorText = ref('二维码加载失败')

    /** 是否已销毁，劫持异步回调，避免组件卸载后还操作 DOM/状态 */
    let isDestroyed = false
    /** message 监听是否已绑定，避免 reload 时重复绑定同一个回调 */
    let isMessageListenerBound = false
    /** 当前会话的 state，与 sessionStorage 双写以做校验 */
    let currentState = ''
    /**
     * 是否已处理过一次回调（成功或失败均算）
     * 用于幂等保护，避免中转页 postMessage 被多次触发时，
     * 重复调用 handleLoginSuccess → 重复触发客户端 winClose / 拉起新登录窗
     */
    let hasHandledCallback = false
    /** 当前正在等待 iframe 加载的控制器，用于 reload / 卸载时主动释放 observer 和定时器 */
    let currentIframeReadyWaiter: WxLoginIframeReadyWaiter | null = null

    preloadWxLoginSdk(props.appid)

    /**
     * 完整的 redirect_uri（永远指向生产中转页，附带 postmessage_domain 参数）
     * - 中转页拿到 code 后直接 postMessage 给当前业务页，不再进行业务域二跳
     * - 避免客户端 webview 把 iframe 内的中转页二跳误判为登录窗主页面导航
     */
    const computedRedirectUri = computed(() => {
      const businessOrigin = window.location.origin
      const params = `postmessage_domain=${encodeURIComponent(businessOrigin)}`
      return `${props.proxyUrl}?${params}`
    })

    /**
     * 允许接收回调消息的来源：
     * - 同源：兼容历史两段跳逻辑
     * - proxyUrl 源：新逻辑由生产中转页直接 postMessage 回当前业务页
     */
    const allowedCallbackOrigins = computed(() => {
      const origins = [window.location.origin]
      const proxyOrigin = getUrlOrigin(props.proxyUrl)

      if (proxyOrigin && !origins.includes(proxyOrigin)) {
        origins.push(proxyOrigin)
      }

      return origins
    })

    /** 外层容器样式：根据 size 控制宽高，便于外部按设计稿尺寸接入 */
    const containerStyle = computed(() => ({
      width: `${props.size}px`,
      height: `${props.size}px`
    }))

    /**
     * @description 渲染微信扫码二维码
     */
    async function renderQrcode() {
      cancelWxLoginIframeReadyWaiter()
      status.value = 'loading'
      loadingText.value = '正在加载微信扫码...'
      errorText.value = '二维码加载失败'
      // 重置幂等标志：每次重新拉起二维码都视为一次新会话，允许处理一次新的回调
      hasHandledCallback = false
      addCallbackListener()

      // 业务页 origin 必须在白名单内（否则中转页二跳回来时会被拦截）
      // 提前在客户端校验一次，可以让本地/测试环境配置错误时直接看到提示，避免扫码后才发现
      if (!isBusinessOriginAllowed(window.location.origin)) {
        status.value = 'error'
        errorText.value = '当前域名未在登录白名单内'
        // eslint-disable-next-line no-console
        console.error('[WxWebLogin] business origin not in whitelist:', window.location.origin)
        return
      }

      try {
        await loadWxLoginSdk()
        if (isDestroyed) return

        if (typeof window.WxLogin !== 'function') {
          throw new Error('WxLogin 构造函数缺失')
        }

        // 生成并存储 state
        currentState = generateState()
        try {
          window.sessionStorage.setItem(STATE_STORAGE_KEY, currentState)
        } catch (err) {
          // sessionStorage 不可用（隐私模式等）：仅依赖闭包变量校验
        }

        // 等待下一帧确保挂载点 DOM 已渲染
        await new Promise((resolve) => requestAnimationFrame(resolve))
        if (isDestroyed) return

        const container = document.getElementById(containerId)
        if (!container) {
          throw new Error('二维码挂载点不存在')
        }

        // 清空容器，避免重复 mount 时旧 iframe 残留
        container.innerHTML = ''

        // 先开始监听 iframe 注入和加载，再实例化 wxLogin，避免错过 iframe load 事件。
        const iframeReady = waitForWxLoginIframeReady(container)
        currentIframeReadyWaiter = iframeReady

        // 实例化 wxLogin.js，二维码 iframe 会被注入到 #containerId 节点内
        // wxLogin.js 内部已持有自身引用，无需保存返回值
        // eslint-disable-next-line no-new
        new (window.WxLogin as any)({
          // self_redirect=true：扫码后回跳发生在 iframe 内部，父页不刷新
          // 中转页拿到 code 后通过 postMessage 推回父页（详见 public/wx-web-login-callback.html）
          self_redirect: true,
          id: containerId,
          appid: props.appid,
          scope: props.scope,
          redirect_uri: encodeURIComponent(computedRedirectUri.value),
          state: currentState,
          // 二维码主题色
          style: 'black',
          // 自定义 iframe 内样式表（可选）；为空时使用 wxLogin.js 默认样式
          href: props.customCssUrl || ''
        })

        await iframeReady.promise
        if (currentIframeReadyWaiter !== iframeReady) return
        currentIframeReadyWaiter = null
        if (isDestroyed) return

        status.value = 'ready'
      } catch (err) {
        cancelWxLoginIframeReadyWaiter()
        if (isDestroyed) return
        status.value = 'error'
        errorText.value = '二维码加载失败'
        // eslint-disable-next-line no-console
        console.error('[WxWebLogin] renderQrcode error:', err)
      }
    }

    /**
     * @description 校验回调消息合法性
     * - 必须与中转页同源
     * - data.type 必须为约定的 CALLBACK_MESSAGE_TYPE
     */
    function isValidCallbackEvent(event: MessageEvent): boolean {
      if (!allowedCallbackOrigins.value.includes(event.origin)) return false
      const data = event.data
      if (!data || typeof data !== 'object') return false
      if (data.type !== CALLBACK_MESSAGE_TYPE) return false
      return true
    }

    /**
     * @description 处理来自中转页的 postMessage
     */
    async function handleCallbackMessage(event: MessageEvent) {
      if (!isValidCallbackEvent(event)) return

      // 幂等保护：同一轮扫码只允许处理一次回调，避免 postMessage 被多次触发时
      // 重复调用 handleLoginSuccess → 重复触发客户端 winClose / 拉起新登录窗
      if (hasHandledCallback) return
      hasHandledCallback = true

      const { code, state } = event.data as { code: string; state: string }

      // 取出存储的 state 进行比对（双重校验：sessionStorage + 闭包变量）
      let storedState = ''
      try {
        storedState = window.sessionStorage.getItem(STATE_STORAGE_KEY) || ''
      } catch (err) {
        storedState = ''
      }
      const expectedState = storedState || currentState

      // state 校验失败：可能为重放攻击或会话错乱，直接报错并要求重新扫码
      if (!state || !expectedState || state !== expectedState) {
        stopCurrentWxSession()
        handleLoginFail(new Error('state 校验失败'), 'state 校验失败，请重新扫码')
        return
      }

      // code 缺失：可能是用户拒绝授权（authdeny）或微信回跳异常
      if (!code) {
        stopCurrentWxSession()
        handleLoginFail(new Error('未获取到授权 code'), '授权失败，请重新扫码')
        return
      }

      // state 一次性使用，立即清除，防止重放
      try {
        window.sessionStorage.removeItem(STATE_STORAGE_KEY)
      } catch (err) {
        // ignore
      }
      currentState = ''

      // 收到合法 code 后，本轮 wxLogin iframe 的使命已经结束。
      // 这里必须在请求后端、触发 winClose 之前立即清理 iframe：网站应用登录的 iframe
      // 此时已停在中转页 URL 上，部分客户端 webview 在关闭登录窗时会受到这个子页面导航影响，
      // 表现为登录窗闪回、二维码区域空白、关闭按钮无法稳定关闭。
      status.value = 'loading'
      loadingText.value = '正在完成登录...'
      stopCurrentWxSession()

      // 调用后端换 token
      // 注意：try/catch 仅包裹接口请求，不能包裹 handleLoginSuccess —— 否则 ocpc/领取活动等
      // 后置异步链路里抛出的任何异常都会被当成"登录失败"处理，导致已经登录成功却显示失败 UI
      // 且无法触发 winClose 关窗（与原公众号登录 LoginService.requestQuickLogin 的写法保持一致）
      let res: FastLoginResponseData
      try {
        res = await service.requestWxWebLogin({ code, meta: { state } })
      } catch (err) {
        // 接口失败：上报登录失败埋点 + UI 提示重试
        login.handleLoginFail(err, login_type_info.wechat)
        handleLoginFail(err, '登录失败，请重新扫码')
        return
      }

      if (isDestroyed) return

      loadingText.value = '登录成功，正在关闭...'

      login.handleLoginSuccess(res, login_type_info.wechat, {
        forceClientWinClose: true
      })
    }

    /**
     * @description 绑定中转页回调监听
     */
    function addCallbackListener() {
      if (isMessageListenerBound) return
      window.addEventListener('message', handleCallbackMessage)
      isMessageListenerBound = true
    }

    /**
     * @description 解绑中转页回调监听
     */
    function removeCallbackListener() {
      if (!isMessageListenerBound) return
      window.removeEventListener('message', handleCallbackMessage)
      isMessageListenerBound = false
    }

    /**
     * @description 结束当前微信网站应用登录会话
     */
    function stopCurrentWxSession() {
      removeCallbackListener()
      destroyWxLoginIframe()
    }

    /**
     * @description 取消正在等待的 iframe 加载监听
     */
    function cancelWxLoginIframeReadyWaiter() {
      if (!currentIframeReadyWaiter) return
      currentIframeReadyWaiter.cancel()
      currentIframeReadyWaiter = null
    }

    /**
     * @description 销毁 wxLogin.js 注入的 iframe
     * 销毁顺序：先把 iframe.src 切到 about:blank 中断当前 navigation，再从 DOM 移除。
     * 这里不能只等组件卸载时清理，因为登录成功后的关窗由客户端/父页面接管，
     * 一旦中转页 iframe 在关窗过程中仍存活，就可能触发登录窗被重新展示。
     */
    function destroyWxLoginIframe() {
      cancelWxLoginIframeReadyWaiter()
      const container = document.getElementById(containerId)
      if (!container) return
      const iframe = container.querySelector('iframe')
      if (iframe) {
        try {
          iframe.src = 'about:blank'
        } catch (e) {
          // 跨域 iframe 设置 src 通常不会抛错，保险起见兜底
        }
      }
      container.innerHTML = ''
    }

    /**
     * @description 统一错误处理：切换 UI 到错误态
     */
    function handleLoginFail(_err: any, displayText: string) {
      if (isDestroyed) return
      status.value = 'error'
      errorText.value = displayText
    }

    /**
     * @description 用户点击错误占位时重新拉起二维码
     */
    function reload() {
      if (status.value === 'loading') return
      renderQrcode()
    }

    onMounted(() => {
      // 入参兜底：appid 缺失直接进入错误态，避免 wxLogin.js 报内部错
      if (!props.appid) {
        status.value = 'error'
        errorText.value = '微信 appid 未配置'
        return
      }
      addCallbackListener()
      renderQrcode()
    })

    onBeforeUnmount(() => {
      isDestroyed = true
      stopCurrentWxSession()

      // 清理可能残留的 state，避免下次进入复用旧值
      try {
        window.sessionStorage.removeItem(STATE_STORAGE_KEY)
      } catch (err) {
        // ignore
      }
    })

    return {
      containerId,
      containerStyle,
      status,
      loadingText,
      errorText,
      reload
    }
  }
})
